// /admin/approvals — pending_agent_actions queue. Admin can approve or
// reject without leaving the portal (calls decide-pending-action edge fn).

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDateGB } from "@/lib/format";
import { getSupabase } from "@/lib/supabase";
import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";

type ActionRow = {
  id: string;
  action_type: string;
  agent_name: string;
  summary: string;
  status: "pending" | "executed" | "rejected" | "execution_failed";
  payload: Record<string, unknown> | null;
  decision_reason: string | null;
  decided_at: string | null;
  created_at: string;
  approval_email_to: string | null;
  execution_result: Record<string, unknown> | null;
};

export function AdminApprovals() {
  const [rows, setRows] = useState<ActionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  const [error, setError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const sb = getSupabase();
    let q = sb
      .from("pending_agent_actions")
      .select(
        "id, action_type, agent_name, summary, status, payload, decision_reason, decided_at, created_at, approval_email_to, execution_result",
      )
      .order("created_at", { ascending: false })
      .limit(200);
    if (filter === "pending") q = q.eq("status", "pending");
    const { data, error: fetchErr } = await q;
    if (fetchErr) setError(fetchErr.message);
    else setRows((data ?? []) as ActionRow[]);
    setLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is stable in scope; we want re-fetch only when filter changes.
  useEffect(() => {
    void load();
  }, [filter]);

  const decide = async (id: string, verdict: "approve" | "reject") => {
    setActingOn(id);
    setError(null);
    try {
      const sb = getSupabase();
      const { data, error: invokeErr } = await sb.functions.invoke<{
        ok: boolean;
        error?: string;
      }>("decide-pending-action", {
        body: { action_id: id, verdict },
      });
      if (invokeErr) {
        setError(invokeErr.message);
        return;
      }
      if (data?.error) {
        setError(data.error);
        return;
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="flex flex-col gap-4 max-w-5xl mx-auto w-full">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Approvals</h1>
          <p className="text-sm text-ink-dim mt-1">
            Things the inbox agents want to do but need a human to sign off first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FilterPill active={filter === "pending"} onClick={() => setFilter("pending")}>
            Pending
          </FilterPill>
          <FilterPill active={filter === "all"} onClick={() => setFilter("all")}>
            All
          </FilterPill>
        </div>
      </div>

      {error ? (
        <p role="alert" className="text-xs text-red-300">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-ink-dim">Loading…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-sm text-ink-dim py-6 text-center">
              {filter === "pending" ? "Nothing waiting on you." : "No actions yet."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((r) => (
            <ActionCard
              key={r.id}
              row={r}
              acting={actingOn === r.id}
              onApprove={() => decide(r.id, "approve")}
              onReject={() => decide(r.id, "reject")}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ActionCard({
  row,
  acting,
  onApprove,
  onReject,
}: {
  row: ActionRow;
  acting: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const isPending = row.status === "pending";
  return (
    <li>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <code className="text-xs px-1.5 py-0.5 rounded-md border border-line bg-bg-card text-ink-dim">
              {row.action_type}
            </code>
            <StatusBadge status={row.status} />
            <span className="text-xs text-ink-dim ml-auto">{formatDateGB(row.created_at)}</span>
          </CardTitle>
          <CardDescription>{row.summary}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {row.payload ? (
            <details className="text-xs">
              <summary className="cursor-pointer text-ink-dim hover:text-ink">Payload</summary>
              <pre className="mt-2 p-2 rounded-md bg-bg-card border border-line overflow-x-auto text-[11px] leading-snug">
                {JSON.stringify(row.payload, null, 2)}
              </pre>
            </details>
          ) : null}
          {row.decision_reason ? (
            <p className="text-xs text-ink-dim">
              <span className="font-medium text-ink">Decision:</span> {row.decision_reason}
            </p>
          ) : null}
          {row.execution_result?.inbound_body_text ? (
            <p className="text-xs text-ink-dim">
              <span className="font-medium text-ink">Reply:</span>{" "}
              {String(row.execution_result.inbound_body_text)}
            </p>
          ) : null}
          {isPending ? (
            <div className="flex gap-2">
              <Button onClick={onApprove} disabled={acting}>
                <Check size={14} /> {acting ? "Working…" : "Approve"}
              </Button>
              <Button variant="ghost" onClick={onReject} disabled={acting}>
                <X size={14} /> Reject
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </li>
  );
}

function StatusBadge({ status }: { status: ActionRow["status"] }) {
  const map: Record<ActionRow["status"], { label: string; className: string }> = {
    pending: { label: "Pending", className: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
    executed: {
      label: "Executed",
      className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    },
    rejected: { label: "Rejected", className: "bg-red-500/15 text-red-300 border-red-500/30" },
    execution_failed: {
      label: "Failed",
      className: "bg-red-500/15 text-red-300 border-red-500/30",
    },
  };
  const it = map[status];
  return (
    <span
      className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${it.className}`}
    >
      {it.label}
    </span>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1 rounded-full border transition ${
        active
          ? "bg-accent text-accent-on border-accent"
          : "bg-bg-card text-ink-dim border-line hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}
